import { CompanySelect } from "../lib/companies";
import { useEffect, useMemo, useState, useRef } from "react";
import axios from "axios";
import {
  Save, Trash2, Pencil, Paperclip, ExternalLink, AlertTriangle, MapPin,
} from "lucide-react";
import { money, money2, daysLeft, calcCommission, branchRateOf, effectiveRate, calcAgentCommission } from "../lib/finance";
import { API } from "../lib/apiClient";
import { Modal, Field, Select } from "./common";
import { normalizePlate, isValidPlate, plateToProvince } from "../lib/plate";

export function PolicyModal({ branches, customers, settings, initial, presetCustomer, onSave, onClose, pendingPdfFile, setPendingPdfFile, openPolicyPdf, uploadPdfToExistingPolicy, removePdfFromPolicy, updatePdfLabel }) {
  const defaultBranch = branches[0]?.name || "";
  const defaultRate = branches[0]?.commissionRate ?? 15;
  const [form, setForm] = useState(
    initial
      ? { ...initial, plate: initial.plate || "", commissionRate: initial.commissionRate ?? branchRateOf(initial.branch, branches), netPremium: initial.netPremium != null && initial.netPremium !== "" ? String(initial.netPremium) : "", status: initial.status || "aktif", cancellationRefund: initial.cancellationRefund != null ? String(initial.cancellationRefund) : "", cancellationDate: initial.cancellationDate || "" }
      : { policeNo: "", customer: presetCustomer || customers[0]?.name || "", branch: defaultBranch, company: "", plate: "", start: new Date().toISOString().slice(0, 10), end: "", premium: "", netPremium: "", commissionRate: String(defaultRate), payment: "Bekliyor", status: "aktif", cancellationRefund: "", cancellationDate: "", note: "" }
  );
  const [pdfBusy, setPdfBusy] = useState(false);
  // Track if user manually edited net prim — if they did, we stop auto-syncing from brüt.
  const [netTouched, setNetTouched] = useState(() => initial?.netPremium != null && initial?.netPremium !== "");
  const change = (e) => {
    const { name, value } = e.target;
    setForm((f) => {
      const next = { ...f, [name]: name === "plate" ? value.toUpperCase() : value };
      if (name === "branch") {
        next.commissionRate = String(branchRateOf(value, branches));
      }
      return next;
    });
    if (name === "netPremium") setNetTouched(true);
  };

  const preview = calcCommission(form.premium, form.commissionRate, settings.netDivisor, settings.personalShare, form.netPremium, form.status === "iptal" ? form.cancellationRefund : null);
  const autoNet = Number(form.premium) > 0 && Number(settings.netDivisor) > 0 ? Number(form.premium) / Number(settings.netDivisor) : 0;
  const effectiveNetPlaceholder = autoNet > 0 ? autoNet.toFixed(2) : "Brüt / " + settings.netDivisor;
  const resetNetToAuto = () => { setForm((f) => ({ ...f, netPremium: "" })); setNetTouched(false); };
  // Combined attachment list: new pdfs[] array; fallback to legacy pdfPath as single "legacy" entry.
  const attachments = (() => {
    const list = Array.isArray(form.pdfs) ? [...form.pdfs] : [];
    if (!list.length && form.pdfPath) {
      list.push({ id: "legacy", path: form.pdfPath, filename: form.pdfFilename || "Ekli PDF", label: "Ana poliçe", uploadedAt: "" });
    }
    return list;
  })();
  const [newLabel, setNewLabel] = useState("");
  const [editingAttId, setEditingAttId] = useState(null);
  const [editingLabel, setEditingLabel] = useState("");

  const startEditLabel = (att) => {
    setEditingAttId(att.id);
    setEditingLabel(att.label || "");
  };
  const cancelEditLabel = () => { setEditingAttId(null); setEditingLabel(""); };
  const saveEditLabel = async (attId) => {
    if (!initial?.id || !updatePdfLabel) return;
    setPdfBusy(true);
    try {
      const updated = await updatePdfLabel(initial.id, attId, editingLabel.trim());
      setForm((f) => ({ ...f, pdfs: updated.pdfs || [], pdfPath: updated.pdfPath, pdfFilename: updated.pdfFilename }));
      cancelEditLabel();
    } catch (err) {
      window.alert(`Etiket güncellenemedi: ${err?.response?.data?.detail || err?.message || err}`);
    } finally {
      setPdfBusy(false);
    }
  };

  const attachToExisting = async (e) => {
    const file = e.target.files?.[0];
    if (!file || !initial?.id) return;
    setPdfBusy(true);
    try {
      const label = newLabel.trim() || (attachments.length === 0 ? "Ana poliçe" : `Zeyil ${attachments.length}`);
      const updated = await uploadPdfToExistingPolicy(initial.id, file, label);
      setForm((f) => ({ ...f, pdfs: updated.pdfs || [], pdfPath: updated.pdfPath, pdfFilename: updated.pdfFilename }));
      setNewLabel("");
    } catch (err) {
      window.alert(`PDF eklenemedi: ${err?.response?.data?.detail || err?.message || err}`);
    } finally {
      setPdfBusy(false);
      // Reset input so same file can be picked again
      if (e.target) e.target.value = "";
    }
  };

  const detachAttachment = async (attId) => {
    if (!initial?.id) return;
    if (!window.confirm("Bu PDF ekini kaldırmak istediğinize emin misiniz?")) return;
    setPdfBusy(true);
    try {
      const updated = await removePdfFromPolicy(initial.id, attId);
      setForm((f) => ({ ...f, pdfs: updated.pdfs || [], pdfPath: updated.pdfPath, pdfFilename: updated.pdfFilename }));
    } catch (err) {
      window.alert("PDF kaldırılamadı");
    } finally {
      setPdfBusy(false);
    }
  };

  return (
    <Modal title={initial?.id ? "Poliçeyi düzenle" : (initial ? "PDF'den alanları onayla" : "Yeni poliçe ekle")} onClose={onClose}>
      <form onSubmit={(e) => { e.preventDefault(); onSave(form); }}>
        <div className="form-grid">
          <Field label="Poliçe numarası" name="policeNo" value={form.policeNo} onChange={change} required />
          <CompanySelect value={form.company} onChange={change} required />
          <label className="field">
            <span>Plaka</span>
            <input
              data-testid="plate-input"
              name="plate"
              value={form.plate ?? ""}
              onChange={change}
              placeholder="34 ABC 123"
            />
            {form.plate && !isValidPlate(form.plate) ? (
              <em className="field-hint plate-warning" data-testid="plate-warning">
                <AlertTriangle size={11} /> Standart TR plaka değil (ör: 34 ABC 123). Yine de kaydedebilirsiniz.
              </em>
            ) : plateToProvince(form.plate) ? (
              <em className="field-hint" data-testid="policy-plate-province"><MapPin size={11} /> {plateToProvince(form.plate)}</em>
            ) : (
              <em className="field-hint">Araç poliçeleri için opsiyonel. Aynı plakalı poliçeler "Araçlar" sayfasında gruplanır.</em>
            )}
          </label>
          <label className="field">
            <span>Müşteri</span>
            <input
              data-testid="customer-input"
              list="customer-datalist"
              name="customer"
              value={form.customer ?? ""}
              onChange={change}
              placeholder="Müşteri seçin veya yeni ad yazın"
              required
            />
            <datalist id="customer-datalist">
              {customers.map((c) => <option key={c.id} value={c.name} />)}
            </datalist>
            <em className="field-hint">Yeni bir isim yazarsanız kayıtta otomatik müşteri oluşturulur</em>
          </label>
          <Select label="Branş" name="branch" value={form.branch} onChange={change} options={branches.map((b) => b.name)} />
          <Field label="Başlangıç tarihi" name="start" type="date" value={form.start} onChange={change} required />
          <Field label="Bitiş tarihi" name="end" type="date" value={form.end} onChange={change} required />
          <Field label="Brüt Prim (₺)" name="premium" type="number" value={form.premium} onChange={change} required />
          <label className="field">
            <span>Net Prim (₺)</span>
            <input
              data-testid="net-premium-input"
              name="netPremium"
              type="number"
              step="0.01"
              value={form.netPremium ?? ""}
              onChange={change}
              placeholder={effectiveNetPlaceholder}
            />
            {netTouched && Number(form.netPremium) > 0 ? (
              <em className="field-hint" data-testid="net-manual-hint" style={{ color: "#059669" }}>Manuel girildi · Brüt/{settings.netDivisor} formülü yoksayılır. <button type="button" className="text-button" onClick={resetNetToAuto} style={{ padding: 0, fontSize: 11 }}>Otomatik hesapla</button></em>
            ) : (
              <em className="field-hint">Boş bırakırsan Brüt ÷ {settings.netDivisor} otomatik hesaplanır. Kasko gibi değişken orana sahip poliçelerde buraya PDF'deki net primi yazın.</em>
            )}
          </label>
          <Field label="Komisyon oranı (%)" name="commissionRate" type="number" step="0.5" value={form.commissionRate} onChange={change} hint="Varsayılan branş oranından geldi" required />
          <Select label="Ödeme durumu" name="payment" value={form.payment} onChange={change} options={["Bekliyor", "Ödendi", "Kısmi ödendi"]} />
          <Select label="Poliçe durumu" name="status" value={form.status || "aktif"} onChange={change} options={["aktif", "iptal"]} data-testid="policy-status-select" />
          {form.status === "iptal" && (
            <>
              <label className="field">
                <span>İade tutarı (₺) — opsiyonel</span>
                <input
                  data-testid="policy-refund-input"
                  name="cancellationRefund"
                  type="number"
                  step="0.01"
                  min="0"
                  value={form.cancellationRefund ?? ""}
                  onChange={change}
                  placeholder="Örn. 450"
                />
                <em className="field-hint">Girilirse bu tutar kişisel hak edişten düşülür. Boş bırakılabilir.</em>
              </label>
              <label className="field">
                <span>İptal tarihi (opsiyonel)</span>
                <input
                  data-testid="policy-cancellation-date-input"
                  name="cancellationDate"
                  type="date"
                  value={form.cancellationDate || ""}
                  onChange={change}
                />
              </label>
            </>
          )}
        </div>
        <label className="field full">
          <span>Notlar</span>
          <textarea data-testid="policy-note-input" name="note" value={form.note} onChange={change} />
        </label>
        <div className="pdf-attachment" data-testid="pdf-attachment">
          <div className="pdf-attachment-head">
            <Paperclip size={15} /> <span>Poliçe PDF ekleri</span>
            {attachments.length > 0 && <span style={{ marginLeft: 8, fontSize: 11, color: "#64748b" }}>{attachments.length} dosya</span>}
          </div>
          {attachments.map((a, idx) => (
            <div className="pdf-attachment-row" key={a.id || idx} data-testid={`pdf-attachment-${a.id || idx}`}>
              <span className="pdf-attachment-name" style={{ flex: 1 }}>
                {a.filename || "Ekli PDF"}
                {editingAttId === a.id ? (
                  <span style={{ display: "inline-flex", gap: 6, alignItems: "center", marginLeft: 8 }}>
                    <input
                      data-testid={`pdf-label-edit-input-${a.id}`}
                      value={editingLabel}
                      onChange={(e) => setEditingLabel(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); saveEditLabel(a.id); } if (e.key === "Escape") cancelEditLabel(); }}
                      placeholder="Örn. Zeyil 2"
                      autoFocus
                      style={{ padding: "3px 8px", borderRadius: 4, border: "1px solid #cbd5e1", fontSize: 11, width: 140 }}
                    />
                    <button type="button" className="text-button" data-testid={`pdf-label-save-${a.id}`} onClick={() => saveEditLabel(a.id)} disabled={pdfBusy} style={{ padding: "2px 6px", fontSize: 11, color: "#059669" }}>Kaydet</button>
                    <button type="button" className="text-button" onClick={cancelEditLabel} style={{ padding: "2px 6px", fontSize: 11 }}>Vazgeç</button>
                  </span>
                ) : (
                  <>
                    {a.label && <span style={{ marginLeft: 8, fontSize: 10, padding: "2px 6px", borderRadius: 4, background: "#e0f2fe", color: "#0369a1", fontWeight: 600 }}>{a.label}</span>}
                    {initial?.id && updatePdfLabel && <button type="button" className="text-button" data-testid={`pdf-label-edit-${a.id || idx}`} onClick={() => startEditLabel(a)} title="Etiketi düzenle" style={{ padding: "0 4px", marginLeft: 6, color: "#64748b" }}><Pencil size={11} /></button>}
                  </>
                )}
              </span>
              <button type="button" className="text-button" data-testid={`pdf-attachment-open-${a.id || idx}`} onClick={() => openPolicyPdf(initial.id, a.id === "legacy" ? null : a.id)}><ExternalLink size={13} /> Önizle</button>
              {initial?.id && <button type="button" className="text-button danger" data-testid={`pdf-attachment-delete-${a.id || idx}`} onClick={() => detachAttachment(a.id)} disabled={pdfBusy}><Trash2 size={13} /> Kaldır</button>}
            </div>
          ))}
          {pendingPdfFile && !initial?.id && (
            <div className="pdf-attachment-row">
              <span className="pdf-attachment-name">{pendingPdfFile.name} · kayıtla birlikte yüklenecek</span>
              <button type="button" className="text-button danger" onClick={() => setPendingPdfFile && setPendingPdfFile(null)}>Kaldır</button>
            </div>
          )}
          {initial?.id ? (
            <div style={{ display: "flex", gap: 8, marginTop: 10, alignItems: "flex-end", flexWrap: "wrap" }}>
              <label className="field" style={{ flex: "1 1 180px", minWidth: 180 }}>
                <span>Yeni PDF etiketi (opsiyonel)</span>
                <input
                  data-testid="pdf-new-label-input"
                  value={newLabel}
                  onChange={(e) => setNewLabel(e.target.value)}
                  placeholder={attachments.length === 0 ? "Ana poliçe" : `Zeyil ${attachments.length}`}
                />
              </label>
              <label className="pdf-attachment-upload" style={{ marginBottom: 2 }}>
                <input type="file" accept="application/pdf,.pdf" data-testid="policy-pdf-upload" onChange={attachToExisting} disabled={pdfBusy} />
                <span>{pdfBusy ? "Yükleniyor…" : (attachments.length === 0 ? "PDF ekle" : "+ Yeni PDF ekle (zeyil)")}</span>
              </label>
            </div>
          ) : attachments.length === 0 && !pendingPdfFile && (
            <p className="privacy-note">Kayıt sonrasında bu ekrandan PDF (ana poliçe + zeyiller) ekleyebilirsiniz.</p>
          )}
        </div>
        <div className="commission-preview" data-testid="commission-preview">
          <div><small>Brüt</small><strong>{money2(preview.brut)}</strong></div>
          <div><small>Net</small><strong>{money2(preview.net)}</strong></div>
          <div><small>Acente</small><strong>{money2(preview.agency)}</strong></div>
          <div><small>{form.status === "iptal" && preview.refund > 0 ? "Kişisel (iade sonrası)" : "Kişisel"}</small><strong className="commission">{money2(preview.personal)}</strong>{form.status === "iptal" && preview.refund > 0 && <em style={{ display: "block", fontSize: 10, color: "#be123c", marginTop: 2 }}>−{money2(preview.refund)} iade</em>}</div>
        </div>
        <div className="modal-actions">
          <button type="button" className="outline-button" data-testid="policy-modal-cancel" onClick={onClose}>Vazgeç</button>
          <button className="primary-button" data-testid="policy-modal-save">{initial?.id ? "Değişiklikleri kaydet" : "Poliçeyi kaydet"}</button>
        </div>
      </form>
    </Modal>
  );
}

